import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  ListSubheader,
  Typography
} from '@mui/material';
import { Add } from '@mui/icons-material';

const buildDisplayOptions = (mapping, currentValue) => {
  const entries = Object.entries(mapping || {});
  const seen = new Set(entries.map(([code]) => code));
  if (currentValue && !seen.has(currentValue)) {
    entries.unshift([currentValue, currentValue]);
  }
  return entries.sort(([a], [b]) => a.localeCompare(b));
};

const NatureOfFlightSelect = ({
  value,
  onChange,
  disabled = false,
  natureOfFlightMapping = {},
  onAddMapping,
  fullWidth = true,
  label = 'Nature of Flight'
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const options = buildDisplayOptions(natureOfFlightMapping, value);

  const handleSelectChange = (event) => {
    onChange(event.target.value);
  };

  const openAddDialog = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDialogError('');
    setNewCode({ code: '', description: '' });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (saving) return;
    setDialogOpen(false);
    setDialogError('');
  };

  const handleSaveNewCode = async () => {
    const code = newCode.code.trim().toUpperCase();
    const description = newCode.description.trim();
    if (!code || !description) {
      setDialogError('Both code and description are required');
      return;
    }
    if (!/^[A-Z0-9_-]{1,10}$/.test(code)) {
      setDialogError('Code must be 1–10 characters (letters, numbers, - or _)');
      return;
    }

    try {
      setSaving(true);
      setDialogError('');
      await onAddMapping(code, description);
      onChange(code);
      setDialogOpen(false);
      setNewCode({ code: '', description: '' });
    } catch (error) {
      setDialogError(error.response?.data?.message || error.message || 'Failed to save code');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <FormControl fullWidth={fullWidth} disabled={disabled}>
        <InputLabel sx={{ whiteSpace: 'nowrap' }}>{label}</InputLabel>
        <Select
          value={value || ''}
          label={label}
          onChange={handleSelectChange}
          sx={{ '& .MuiSelect-select': { minWidth: '200px' } }}
        >
          <MenuItem value="">
            <em>Select...</em>
          </MenuItem>
          {options.map(([code, desc]) => (
            <MenuItem key={code} value={code}>
              {desc && desc !== code ? `${code} - ${desc}` : code}
            </MenuItem>
          ))}
          {!disabled && onAddMapping && (
            <>
              <Divider />
              <ListSubheader sx={{ lineHeight: '32px' }}>Custom</ListSubheader>
              <MenuItem onMouseDown={openAddDialog}>
                <Add fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Add new code...
              </MenuItem>
            </>
          )}
        </Select>
      </FormControl>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>Add Nature of Flight Code</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Code"
            placeholder="e.g. TR"
            value={newCode.code}
            onChange={(e) => setNewCode((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
            disabled={saving}
            helperText="Short code (max 10 characters)"
            inputProps={{ maxLength: 10 }}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Description"
            placeholder="e.g. Training Flight"
            value={newCode.description}
            onChange={(e) => setNewCode((prev) => ({ ...prev, description: e.target.value }))}
            disabled={saving}
          />
          {dialogError && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {dialogError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSaveNewCode} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save & Select'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NatureOfFlightSelect;
